/**
 * SYSTEM SETTINGS MODULE — Point of Sale Setup.
 * Two tabs, ported from the source app's admin POSSetupSection:
 *   • POS Items          — the catalog of POS materials (name, inventory,
 *                          min order qty, price, shipping, tax, image).
 *   • Delivery Addresses — per state/distributor warehouse address book that
 *                          POS orders ship to (the "distributor warehouse
 *                          location / address / contact email" grid).
 *   • Order Form Embed   — one public order form per distributor, pasted into
 *                          the customer's own site (backlog item 28).
 *
 * Both read/write account-scoped reference data through shared/referenceDataService
 * (the same tables the POS & Fulfillment module consumes). State + distributor
 * options come from the shared sales_states catalog (States & Distributors).
 * Workspace owner/admin only (route-gated by RequireWorkspaceAdmin).
 */

import { useEffect, useRef, useState } from "react";
import PosEmbedKeysPanel from "./PosEmbedKeysPanel.jsx";
import {
  loadPOSItems, createPOSItem, updatePOSItem, updatePOSItemImageUrl, deletePOSItem, uploadPOSItemImage,
  signPOSItemImages, signPOSItemImage,
  loadPOSDeliveryContacts, createPOSDeliveryContact, updatePOSDeliveryContact, deletePOSDeliveryContact,
  listSalesStates,
} from "../../../shared/utils/referenceDataService.js";

const cell = { padding: "9px 10px", borderBottom: "1px solid var(--border)", verticalAlign: "top" };
const num = (v) => (v === "" || v == null ? "" : String(v));
const money = (v) => (v == null || v === "" ? "—" : `$${Number(v).toFixed(2)}`);

export default function PointOfSaleSetupSection() {
  const [tab, setTab] = useState("items");
  return (
    <section className="settings-card wide">
      <h2 style={{ marginTop: 0 }}>Point of Sale Setup</h2>
      <p className="muted">
        Set up the POS materials your team can order and the distributor warehouses
        they ship to. The POS &amp; Fulfillment module reads these when building orders.
      </p>
      <div style={{ display: "flex", gap: 8, margin: "4px 0 18px" }}>
        <button type="button" className={tab === "items" ? "" : "secondary"} onClick={() => setTab("items")}>POS Items</button>
        <button type="button" className={tab === "delivery" ? "" : "secondary"} onClick={() => setTab("delivery")}>Delivery Addresses</button>
        <button type="button" className={tab === "embed" ? "" : "secondary"} onClick={() => setTab("embed")}>Order Form Embed</button>
      </div>
      {tab === "items" && <POSItemsPanel />}
      {tab === "delivery" && <DeliveryAddressesPanel />}
      {/* kind is explicit: the same panel serves the sample-request keys from
          Product Sample Orders Setup, and the two must never share a list —
          a sample key has no delivery contact to edit here. */}
      {tab === "embed" && <PosEmbedKeysPanel kind="pos" />}
    </section>
  );
}

// ── POS Items ────────────────────────────────────────────────────────────────
const EMPTY_ITEM = { name: "", description: "", inv: "", minQty: "", price: "", shipping: "", tax: "" };

function POSItemsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [add, setAdd] = useState(EMPTY_ITEM);
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(EMPTY_ITEM);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  async function load() {
    setErr(null);
    // pos-item-images is private (migration 0069), so image_url holds a storage
    // path — resolve them to short-lived signed URLs in one batched call.
    try { setItems(await signPOSItemImages(await loadPOSItems())); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function run(fn) {
    setBusy(true); setErr(null);
    try { await fn(); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  // Tax defaults to 7.5% of price when left blank (matches the source app).
  const resolveTax = (tax, price) =>
    tax !== "" ? tax : price !== "" ? String((parseFloat(price) * 0.075).toFixed(4)) : "";

  async function handleAdd() {
    if (!add.name.trim()) { setErr("Item name is required."); return; }
    await run(async () => {
      const maxSort = items.reduce((m, i) => Math.max(m, i.sort_order ?? 0), 0);
      await createPOSItem({
        name: add.name, description: add.description, sortOrder: maxSort + 1,
        currentInventory: parseInt(add.inv, 10) || 0, minOrderQty: add.minQty,
        pricePerUnit: add.price, shippingCost: add.shipping, taxPerUnit: resolveTax(add.tax, add.price),
      });
      setAdd(EMPTY_ITEM);
    });
  }

  function startEdit(it) {
    setEditId(it.id);
    setEditRow({
      name: it.name ?? "", description: it.description ?? "",
      inv: num(it.current_inventory ?? 0), minQty: num(it.min_order_quantity),
      price: num(it.price_per_unit), shipping: num(it.shipping_cost),
      tax: it.tax_per_unit != null ? num(it.tax_per_unit) : (it.price_per_unit != null ? String((parseFloat(it.price_per_unit) * 0.075).toFixed(4)) : ""),
    });
  }
  async function saveEdit(id) {
    if (!editRow.name.trim()) { setErr("Name is required."); return; }
    await run(async () => {
      await updatePOSItem(id, {
        name: editRow.name, description: editRow.description,
        currentInventory: parseInt(editRow.inv, 10) || 0, minOrderQty: editRow.minQty,
        pricePerUnit: editRow.price, shippingCost: editRow.shipping, taxPerUnit: editRow.tax,
      });
      setEditId(null);
    });
  }

  async function duplicate(it) {
    await run(async () => {
      const maxSort = items.reduce((m, i) => Math.max(m, i.sort_order ?? 0), 0);
      await createPOSItem({
        name: `Copy of ${it.name}`, description: it.description ?? "", sortOrder: maxSort + 1,
        currentInventory: it.current_inventory ?? 0, minOrderQty: it.min_order_quantity,
        pricePerUnit: it.price_per_unit, shippingCost: it.shipping_cost, taxPerUnit: it.tax_per_unit,
      });
    });
  }

  async function onImage(it, file) {
    if (!file) return;
    setUploadingId(it.id); setErr(null);
    try {
      // Persist the storage PATH; render through a signed URL.
      const path = await uploadPOSItemImage(it.item_key, file);
      await updatePOSItemImageUrl(it.id, path);
      const src = await signPOSItemImage(path);
      setItems((prev) => prev.map((i) => (i.id === it.id ? { ...i, image_url: path, image_src: src } : i)));
    } catch (e) { setErr(e.message); }
    finally { setUploadingId(null); }
  }

  const fld = { flex: "1 1 120px", margin: 0 };

  return (
    <>
      {err && <p className="error">{err}</p>}

      {/* Add item */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", margin: "0 0 18px" }}>
        <label style={{ flex: "1.4 1 160px", margin: 0 }}>Item Name
          <input value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} placeholder="e.g. Floor Display" />
        </label>
        <label style={{ flex: "1.4 1 160px", margin: 0 }}>Description
          <input value={add.description} onChange={(e) => setAdd({ ...add, description: e.target.value })} placeholder="Brief description" />
        </label>
        <label style={fld}>Start Inv.
          <input type="number" min="0" value={add.inv} onChange={(e) => setAdd({ ...add, inv: e.target.value })} placeholder="0" />
        </label>
        <label style={fld}>Min Qty
          <input type="number" min="0" value={add.minQty} onChange={(e) => setAdd({ ...add, minQty: e.target.value })} placeholder="—" />
        </label>
        <label style={fld}>Price ($)
          <input type="number" min="0" step="0.01" value={add.price} onChange={(e) => setAdd({ ...add, price: e.target.value })} placeholder="0.00" />
        </label>
        <label style={fld}>Shipping ($)
          <input type="number" min="0" step="0.01" value={add.shipping} onChange={(e) => setAdd({ ...add, shipping: e.target.value })} placeholder="0.00" />
        </label>
        <label style={fld}>Tax ($)
          <input type="number" min="0" step="0.0001" value={add.tax} onChange={(e) => setAdd({ ...add, tax: e.target.value })} placeholder="auto 7.5%" />
        </label>
        <button className="btn" type="button" disabled={busy || !add.name.trim()} onClick={handleAdd} style={{ alignSelf: "flex-end" }}>Add Item</button>
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">No POS items yet — add your first one above.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th style={cell}>Image</th>
                <th style={cell}>Item</th>
                <th style={cell}>Inventory</th>
                <th style={cell}>Min Qty</th>
                <th style={cell}>Price</th>
                <th style={cell}>Shipping</th>
                <th style={cell}>Tax</th>
                <th style={{ ...cell, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const editing = editId === it.id;
                return (
                  <tr key={it.id}>
                    <td style={cell}>
                      {it.image_src
                        ? <img src={it.image_src} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border)" }} />
                        : <span className="muted">—</span>}
                      <input
                        ref={(el) => (fileRefs.current[it.id] = el)}
                        type="file" accept="image/*" style={{ display: "none" }}
                        onChange={(e) => { onImage(it, e.target.files?.[0]); e.target.value = ""; }}
                      />
                      <div>
                        <button type="button" className="link" disabled={uploadingId === it.id} onClick={() => fileRefs.current[it.id]?.click()} style={{ fontSize: 12 }}>
                          {uploadingId === it.id ? "Uploading…" : (it.image_url ? "Replace" : "Upload")}
                        </button>
                      </div>
                    </td>
                    <td style={cell}>
                      {editing ? (
                        <>
                          <input value={editRow.name} onChange={(e) => setEditRow({ ...editRow, name: e.target.value })} placeholder="Name" />
                          <input value={editRow.description} onChange={(e) => setEditRow({ ...editRow, description: e.target.value })} placeholder="Description" style={{ marginTop: 4 }} />
                        </>
                      ) : (
                        <><strong>{it.name}</strong>{it.description ? <div className="muted" style={{ fontSize: 12 }}>{it.description}</div> : null}</>
                      )}
                    </td>
                    <td style={cell}>{editing ? <input type="number" min="0" value={editRow.inv} onChange={(e) => setEditRow({ ...editRow, inv: e.target.value })} style={{ width: 80 }} /> : (it.current_inventory ?? 0)}</td>
                    <td style={cell}>{editing ? <input type="number" min="0" value={editRow.minQty} onChange={(e) => setEditRow({ ...editRow, minQty: e.target.value })} style={{ width: 70 }} /> : (it.min_order_quantity ?? <span className="muted">—</span>)}</td>
                    <td style={cell}>{editing ? <input type="number" min="0" step="0.01" value={editRow.price} onChange={(e) => setEditRow({ ...editRow, price: e.target.value })} style={{ width: 80 }} /> : money(it.price_per_unit)}</td>
                    <td style={cell}>{editing ? <input type="number" min="0" step="0.01" value={editRow.shipping} onChange={(e) => setEditRow({ ...editRow, shipping: e.target.value })} style={{ width: 80 }} /> : money(it.shipping_cost)}</td>
                    <td style={cell}>{editing ? <input type="number" min="0" step="0.0001" value={editRow.tax} onChange={(e) => setEditRow({ ...editRow, tax: e.target.value })} style={{ width: 80 }} /> : money(it.tax_per_unit)}</td>
                    <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                      {editing ? (
                        <>
                          <button className="btn" type="button" disabled={busy} onClick={() => saveEdit(it.id)} style={{ marginRight: 8 }}>Save</button>
                          <button type="button" className="secondary" disabled={busy} onClick={() => setEditId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="link" disabled={busy} onClick={() => startEdit(it)} style={{ marginRight: 12 }}>Edit</button>
                          <button type="button" className="link" disabled={busy} onClick={() => duplicate(it)} style={{ marginRight: 12 }}>Duplicate</button>
                          <button type="button" className="link" style={{ color: "var(--error)" }} disabled={busy} onClick={() => { if (window.confirm(`Delete "${it.name}"?`)) run(() => deletePOSItem(it.id)); }}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Delivery Addresses (distributor warehouse address book) ──────────────────
const EMPTY_DA = { state: "", distributor: "", warehouseName: "", posContactName: "", posContactPhone: "", posContactEmail: "", posStreetAddress: "", posCity: "", posZipcode: "" };

function DeliveryAddressesPanel() {
  const [rows, setRows] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_DA);
  const [editId, setEditId] = useState(null); // null = closed, "new" = adding, else editing id

  async function load() {
    setErr(null);
    try {
      const [contacts, st] = await Promise.all([loadPOSDeliveryContacts(), listSalesStates()]);
      setRows(contacts); setStates(st);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const stateOptions = [...new Set(states.map((s) => s.state_name).filter(Boolean))].sort();
  const distributorOptions = (selected) => {
    const list = selected ? states.filter((s) => s.state_name === selected) : states;
    return [...new Set(list.map((s) => s.distributor).filter(Boolean))].sort();
  };

  function openAdd() { setForm(EMPTY_DA); setEditId("new"); setErr(null); }
  function openEdit(r) {
    setForm({
      state: r.state || "", distributor: r.distributor || "", warehouseName: r.warehouse_name || "",
      posContactName: r.pos_contact_name || "", posContactPhone: r.pos_contact_phone || "", posContactEmail: r.pos_contact_email || "",
      posStreetAddress: r.pos_street_address || "", posCity: r.pos_city || "", posZipcode: r.pos_zipcode || "",
    });
    setEditId(r.id); setErr(null);
  }

  async function run(fn) {
    setBusy(true); setErr(null);
    try { await fn(); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function save() {
    if (!form.state || !form.distributor) { setErr("State and Distributor are required."); return; }
    await run(async () => {
      if (editId === "new") await createPOSDeliveryContact(form);
      else await updatePOSDeliveryContact(editId, form);
      setEditId(null); setForm(EMPTY_DA);
    });
  }

  const fld = { flex: "1 1 150px", margin: 0 };

  return (
    <>
      {err && <p className="error">{err}</p>}

      {editId == null ? (
        <div style={{ margin: "0 0 14px" }}>
          <button className="btn" type="button" onClick={openAdd}>+ Add Delivery Contact</button>
        </div>
      ) : (
        <div style={{ border: "1px solid var(--border)", borderRadius: "var(--r-control)", padding: 14, margin: "0 0 16px", background: "var(--sunken)" }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>{editId === "new" ? "Add Delivery Contact" : "Edit Delivery Contact"}</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <label style={fld}>State *
              <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value, distributor: "" })}>
                <option value="">— Select —</option>
                {stateOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label style={fld}>Distributor *
              <select value={form.distributor} onChange={(e) => setForm({ ...form, distributor: e.target.value })}>
                <option value="">— Select —</option>
                {distributorOptions(form.state).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label style={fld}>Warehouse Name
              <input value={form.warehouseName} onChange={(e) => setForm({ ...form, warehouseName: e.target.value })} placeholder="Warehouse name" />
            </label>
            <label style={fld}>POS Contact
              <input value={form.posContactName} onChange={(e) => setForm({ ...form, posContactName: e.target.value })} placeholder="Contact name" />
            </label>
            <label style={fld}>Contact Phone
              <input value={form.posContactPhone} onChange={(e) => setForm({ ...form, posContactPhone: e.target.value })} placeholder="Phone" />
            </label>
            <label style={fld}>Contact Email
              <input type="email" value={form.posContactEmail} onChange={(e) => setForm({ ...form, posContactEmail: e.target.value })} placeholder="email@example.com" />
            </label>
            <label style={fld}>Street Address
              <input value={form.posStreetAddress} onChange={(e) => setForm({ ...form, posStreetAddress: e.target.value })} placeholder="Street address" />
            </label>
            <label style={fld}>City
              <input value={form.posCity} onChange={(e) => setForm({ ...form, posCity: e.target.value })} placeholder="City" />
            </label>
            <label style={{ flex: "0 1 110px", margin: 0 }}>Zip
              <input value={form.posZipcode} onChange={(e) => setForm({ ...form, posZipcode: e.target.value })} placeholder="Zip" />
            </label>
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button className="btn" type="button" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
            <button type="button" className="secondary" disabled={busy} onClick={() => { setEditId(null); setForm(EMPTY_DA); }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No delivery contacts yet — add one above.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th style={cell}>State</th>
                <th style={cell}>Distributor</th>
                <th style={cell}>Warehouse</th>
                <th style={cell}>Contact</th>
                <th style={cell}>Phone</th>
                <th style={cell}>Email</th>
                <th style={cell}>Address</th>
                <th style={{ ...cell, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={cell}>{r.state}</td>
                  <td style={cell}>{r.distributor}</td>
                  <td style={cell}>{r.warehouse_name || <span className="muted">—</span>}</td>
                  <td style={cell}>{r.pos_contact_name || <span className="muted">—</span>}</td>
                  <td style={cell}>{r.pos_contact_phone || <span className="muted">—</span>}</td>
                  <td style={cell}>{r.pos_contact_email || <span className="muted">—</span>}</td>
                  <td style={cell}>
                    {[r.pos_street_address, r.pos_city, r.pos_zipcode].filter(Boolean).join(", ") || <span className="muted">—</span>}
                  </td>
                  <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                    <button type="button" className="link" disabled={busy} onClick={() => openEdit(r)} style={{ marginRight: 12 }}>Edit</button>
                    <button type="button" className="link" style={{ color: "var(--error)" }} disabled={busy} onClick={() => { if (window.confirm(`Delete delivery contact for ${r.distributor} (${r.state})?`)) run(() => deletePOSDeliveryContact(r.id)); }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
